/**
 * Pure helper that builds the artefacts a submission produces:
 *   - 1 opportunity record (for the radar)
 *   - 1 deployment-request record (for the approvals queue, stage 1)
 *   - 1 audit-log entry
 *
 * No React, no side-effects. Lets us unit-test the submission shape
 * independently of TenantModeProvider, and keeps the provider small.
 */

const APPROVAL_BY_RISK = {
  high: ["IT Security", "Legal & Compliance", "CAIO", "Board Risk Sub-Committee"],
  med: ["IT Security", "Legal & Compliance", "CAIO"],
  low: ["IT Security", "CAIO"],
};

const RISK_SCORE = { low: 28, med: 55, high: 78 };
const EFFORT_SCORE = { S: 25, M: 55, L: 82 };

function tsIsoNow(now = new Date()) {
  return now.toISOString().replace("T", " ").slice(0, 19);
}

function makeIds(now = new Date()) {
  const oppId = `OPP-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(
    now.getDate(),
  ).padStart(2, "0")}-${String(now.getTime() % 9999).padStart(4, "0")}`;
  const reqId = `DR-${String(now.getTime() % 99999).padStart(5, "0")}`;
  return { oppId, reqId };
}

export function buildOpportunitySubmission(payload, now = new Date()) {
  const ts = tsIsoNow(now);
  const { oppId, reqId } = makeIds(now);

  const roiScore = Math.min(99, Math.round((payload.estAnnualRoiAud || 240000) / 60000));
  const effortScore = EFFORT_SCORE[payload.effort] ?? 55;
  const riskScore = RISK_SCORE[payload.risk] ?? 55;
  const priorityScore = Math.round(
    roiScore * 0.5 + (100 - riskScore) * 0.3 + (100 - effortScore) * 0.2,
  );

  const opportunity = {
    id: oppId,
    rank: 0,
    title: payload.title,
    summary: payload.businessProblem?.slice(0, 140) || "—",
    department: payload.department,
    businessUnit: payload.businessUnit,
    owner: payload.owner,
    status: "new",
    estAnnualRoiAud: payload.estAnnualRoiAud,
    estimatedValueAud: payload.estAnnualRoiAud,
    estimatedInvestmentAud: Math.round((payload.estAnnualRoiAud || 0) * 0.22),
    paybackMonths: payload.paybackMonths || 9,
    timelineWeeks:
      payload.timelineWeeks ||
      (payload.effort === "S" ? 6 : payload.effort === "M" ? 12 : 22),
    effort: payload.effort,
    effortScore,
    risk: payload.risk,
    riskScore,
    roiScore,
    priorityScore,
    complianceImpact: payload.complianceImpact,
    complianceNote: payload.complianceConsiderations,
    recommendedApproach: payload.recommendedApproach,
    recommendedTech: payload.recommendedTech || [],
    sparkline: [40, 48, 56, 60, 66, 72, 78, 84],
    businessProblem: payload.businessProblem,
    proposedSolution: payload.proposedSolution,
    riskAssessment: payload.riskAssessment,
    complianceConsiderations: payload.complianceConsiderations,
    requiredApprovals: payload.requiredApprovals || APPROVAL_BY_RISK[payload.risk] || APPROVAL_BY_RISK.low,
    successMetrics: payload.successMetrics,
    submittedAt: ts,
    submittedByCurrentUser: true,
  };

  const request = {
    id: reqId,
    agentName: payload.title,
    department: payload.department,
    stage: 1,
    risk: payload.risk,
    slaDue: "02d 04h",
    submittedAt: ts,
  };

  const audit = {
    ts,
    actor: payload.actorId || "current-user",
    action: "opportunity.submitted",
    target: oppId,
  };

  return { opportunity, request, audit };
}
